import { parseDateTime } from "@internationalized/date";
import { APIs } from "@website/ticket-ui/common/constants";
import { FormattedCity } from "@website/ticket-ui/common/types";
import { addStSearchInitiatedEvent } from "@website/ticket-ui/dom-flight-search-panel-old/tracking";
import {
  addEventToDataLayer,
  addEventToDataLayerGT3
} from "@website/tracking/tracking.gtm";
import { DATA_LAYER_EVENTS } from "@website/tracking/tracking.types";
import { DatepickerSelectedDateType } from "@website/ui-old/datepicker";
import { getChannel } from "@website/utils/channel/get-channel";
import { daysBetweenDates } from "@website/utils/date-format";
import { useRouter } from "next/router";
import { useCallback, useState } from "react";

export const useSubmit = (
  originItem: FormattedCity | null,
  destinationItem: FormattedCity | null,
  saleType: string,
  date: DatepickerSelectedDateType,
  calendarLocale: string,
  passengerNumber: { adult: number; child: number; infant: number },
  flightClass: string,
  addToHistory: (
    origin: FormattedCity,
    destination: FormattedCity,
    startDate: string,
    endDate: string,
    calendarLocale: string,
    saleType: string,
    passengerNumber: { adult: number; child: number; infant: number }
  ) => void,
  submitValidationCheck: () => boolean,
  searchSource?: "homepage" | "landing" | "nowrouzLanding"
) => {
  const router = useRouter();
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  const onSubmitClick = useCallback(() => {
    if (submitValidationCheck()) {
      setSubmitLoading(true);
      let startDateTime = "";
      let endDateTime = "";
      let startDateObject = null;
      let endDateObject = null;
      let startDate = null;
      let endDate = null;
      if (saleType === "round-trip" && date && date[0] && date[1]) {
        startDateTime = date[0].dateTime;
        endDateTime = date[1].dateTime;

        startDateObject = parseDateTime(startDateTime);
        endDateObject = parseDateTime(endDateTime);

        startDate = `${startDateObject.year}-${startDateObject.month
          .toString()
          .padStart(2, "0")}-${startDateObject.day
          .toString()
          .padStart(2, "0")}`;
        endDate = `${endDateObject.year}-${endDateObject.month
          .toString()
          .padStart(2, "0")}-${endDateObject.day.toString().padStart(2, "0")}`;
      } else if (saleType === "one-way" && date && date[0]) {
        startDateTime = date[0].dateTime;

        startDateObject = parseDateTime(startDateTime);

        startDate = `${startDateObject.year}-${startDateObject.month
          .toString()
          .padStart(2, "0")}-${startDateObject.day
          .toString()
          .padStart(2, "0")}`;
      }

      if (
        originItem &&
        destinationItem &&
        ((startDate && saleType === "one-way") ||
          (endDate && saleType === "round-trip"))
      ) {
        addToHistory(
          originItem,
          destinationItem,
          startDateTime,
          endDateTime,
          calendarLocale,
          saleType,
          passengerNumber
        );
        const { airportName, cityName, airportIataCode, iataCode, cityFaName } =
          originItem ?? {};
        const {
          airportName: destinationAirportName,
          cityName: destinationCityName,
          airportIataCode: destinationAirportIataCode,
          countryName: destinationCountryName,
          iataCode: destinationIataCode,
          cityFaName: destinationCityFaName
        } = destinationItem ?? {};

        const parameters = {
          origin: airportIataCode || iataCode || "",
          origin_airport: airportName || "",
          origin_city: cityName,
          origin_is_city: airportIataCode ? "false" : "true",
          destination: destinationAirportIataCode || destinationIataCode,
          destination_airport: destinationAirportName || "",
          destination_city: destinationCityName,
          destination_country: destinationCountryName,
          destination_is_city: destinationAirportIataCode ? "false" : "true",
          adultCount: `${passengerNumber.adult}`,
          childCount: `${passengerNumber.child}`,
          infantCount: `${passengerNumber.infant}`,
          dateType: calendarLocale === "persian" ? "jalali" : calendarLocale,
          departureDate: startDate || "",
          ...(endDate && { returnDate: endDate }),
          homepage_version: "new",
          cabinType: flightClass,
          isFlightHome: "true",
          refer: "flights-home",
          source: "search_results_page",
          order_by: router?.query?.order_by?.toString() || ""
        };

        const urlParameters = new URLSearchParams(parameters);
        const parameters_ = urlParameters.toString();
        const today = new Date();
        const url =
          `${
            searchSource === "landing" || searchSource === "nowrouzLanding"
              ? "/flights"
              : APIs.pathname || "/flights"
          }/${airportIataCode || iataCode}-${cityName?.replaceAll(" ", "-")}` +
          `/${
            destinationAirportIataCode || destinationIataCode
          }-${destinationCityName?.replaceAll(" ", "-")}` +
          `?${parameters_}`;
        //
        addEventToDataLayerGT3(DATA_LAYER_EVENTS.GENERAL_SEARCH, {
          searchSource: "search_results_page",
          channel: getChannel(),
          originCity: cityName,
          originIsCity: airportIataCode ? "false" : "true",
          originAirport: airportIataCode ? "false" : "true",
          destinationCity: destinationCityName,
          destinationIsCity: destinationAirportIataCode ? "false" : "true",
          destinationAirport: destinationAirportIataCode || destinationIataCode,
          tripType: endDate ? "roundTrip" : "oneWay",
          departureDate: startDate || "",
          returnDepartureDate: endDate || "",
          adultCount: passengerNumber.adult,
          childCount: passengerNumber.child,
          infantCount: passengerNumber.infant,
          passengerCount:
            Number(passengerNumber.adult) +
            Number(passengerNumber.child) +
            Number(passengerNumber.infant),
          calendarType:
            calendarLocale === "persian" ? "jalali" : calendarLocale, // Could be "jalali/gregorian"
          cabinClass: flightClass, // Could be economy/business/firstclass/all
          searchType: "user", // Could be "pricetable/user/direct/crawler"
          searchWindow: startDate
            ? daysBetweenDates(today, new Date(startDate))
            : ""
        });

        addEventToDataLayer(DATA_LAYER_EVENTS.GENERAL_SEARCH, {
          search_source: "search_results_page",
          channel: getChannel(),
          return_departure_date: endDate,
          origin_city: cityName,
          origin_is_city: airportIataCode ? "false" : "true",
          origin_airport: airportIataCode ? "false" : "true",
          destination_city: destinationCityName,
          destination_is_city: destinationAirportIataCode ? "false" : "true",
          destination_airport:
            destinationAirportIataCode || destinationIataCode,
          trip_type: endDate ? "roundTrip" : "oneWay",
          departure_date: startDate || "",
          adultCount: passengerNumber.adult,
          childCount: passengerNumber.child,
          infantCount: passengerNumber.infant,
          route: `${cityFaName} - ${destinationCityFaName}`,
          number_of_passengers:
            Number(passengerNumber.adult) +
            Number(passengerNumber.child) +
            Number(passengerNumber.infant),
          search_calendar_type:
            calendarLocale === "persian" ? "jalali" : calendarLocale, // Could be "jalali/gregorian"
          cabin_type: flightClass, // Could be economy/business/firstclass/all
          searchType: "user", // Could be "pricetable/user/direct/crawler"
          search_window: startDate
            ? daysBetweenDates(today, new Date(startDate))
            : ""
        });

        addStSearchInitiatedEvent(parameters, "dom-flight", undefined);

        window.location.replace(url);
      }
    }
  }, [
    router,
    originItem,
    destinationItem,
    date,
    saleType,
    passengerNumber,
    submitValidationCheck
  ]);

  return {
    onSubmitClick,
    submitLoading,
    submitText:
      searchSource === "landing" || searchSource === "nowrouzLanding"
        ? "جستجو"
        : "بروزرسانی جستجو"
  };
};
